import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Tabs, Tab } from 'react-bootstrap';
import { updateUser, resetUserPassword } from '../../services/api';
import { salutations, countryCodes, usStates, researchAreas, countries } from '../../constants/types';

const UserEditModal = ({ isOpen, onClose, user, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    // Personal Information (matching Profile component)
    salutation: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    secondaryEmail: "",
    workPhone: "",
    workPhoneCountry: "+1",
    cellPhone: "",
    cellPhoneCountry: "+1",
    personalWebPage: "",

    // Primary Affiliation
    primaryAffiliation: {
      institution: "",
      rank: "",
      rankOther: "",
      department: "",
      address: {
        address1: "",
        address2: "",
        city: "",
        postalCode: "",
        country: "",
        state: ""
      }
    },

    // Secondary Affiliation
    secondaryAffiliation: {
      institution: "",
      rank: "",
      rankOther: "",
      department: "",
      address: {
        address1: "",
        address2: "",
        city: "",
        postalCode: "",
        country: "",
        state: ""
      }
    },

    // Areas of Expertise
    areasOfExpertise: {
      primary: ["", "", ""]
    },

    // Removed secondary categories & topics per requirement
    primary1Category: "",
    primary2Category: "",
    primary3Category: "",

    // Removed secondary1Category, secondary2Category
    primary1: "",
    primary2: "",
    primary3: "",

    // Removed secondary1, secondary2

    // Account Information
    username: "",
    role: 'user',
    status: 'active',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [generateRandomPassword, setGenerateRandomPassword] = useState(false);
  const [manualPassword, setManualPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [showPasswordResetConfirm, setShowPasswordResetConfirm] = useState(false);
  const [showPrimaryOtherRank, setShowPrimaryOtherRank] = useState(false);
  const [showSecondaryOtherRank, setShowSecondaryOtherRank] = useState(false);

  const academicRanks = [
    "Professor",
    "Associate Professor", 
    "Assistant Professor",
    "Lecturer",
    "Senior Lecturer",
    "Instructor",
    "PhD Student",
    "Other"
  ];

  // Helper function to find category for a given topic
  const findCategoryForTopic = (topic) => {
    if (!topic) return "";
    for (const [category, topics] of Object.entries(researchAreas)) {
      if (topics.includes(topic)) {
        return category;
      }
    }
    return "";
  };

  useEffect(() => {   
    if (user) {
      // Extract existing expertise data
      const existingPrimary = user.areasOfExpertise?.primary || ["", "", ""];

      setFormData({
        // Personal Information
        salutation: user.salutation || "",
        firstName: user.firstName || "",
        middleName: user.middleName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        secondaryEmail: user.secondaryEmail || "",
        workPhone: user.workPhone || "",
        workPhoneCountry: user.workPhoneCountry || "+1",
        cellPhone: user.cellPhone || "",
        cellPhoneCountry: user.cellPhoneCountry || "+1",
        personalWebPage: user.personalWebPage || "",

        // Primary Affiliation
        primaryAffiliation: user.primaryAffiliation || {
          institution: "",
          rank: "",
          rankOther: "",
          department: "",
          address: {
            address1: "",
            address2: "",
            city: "",
            postalCode: "",
            country: "",
            state: ""
          }
        },

        // Secondary Affiliation
        secondaryAffiliation: user.secondaryAffiliation || {
          institution: "",
          rank: "",
          rankOther: "",
          department: "",
          address: {
            address1: "",
            address2: "",
            city: "",
            postalCode: "",
            country: "",
            state: ""
          }
        },

        // Areas of Expertise
        areasOfExpertise: user.areasOfExpertise || {
          primary: ["", "", ""]
        },

        // Extract categories from existing expertise data
        primary1Category: findCategoryForTopic(existingPrimary[0]),
        primary2Category: findCategoryForTopic(existingPrimary[1]),
        primary3Category: findCategoryForTopic(existingPrimary[2]),

        // Individual topic fields to match AreasOfExpertise component
        primary1: existingPrimary[0] || "",
        primary2: existingPrimary[1] || "",
        primary3: existingPrimary[2] || "",

        // Account Information
        username: user.username || "",
        role: user.role || 'user',
        status: user.status || 'active',
      });
    }
  }, [user]);

  // Monitor rank changes to show/hide "Other" fields
  useEffect(() => {
    setShowPrimaryOtherRank(formData.primaryAffiliation?.rank === "Other");
    setShowSecondaryOtherRank(formData.secondaryAffiliation?.rank === "Other");
  }, [formData.primaryAffiliation?.rank, formData.secondaryAffiliation?.rank]);

  const showAlertMessage = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  const handleCategoryChange = (event, fieldName) => {
    const selectedCategory = event.target.value;
    
    // Use the standard handleInputChange function for category
    const categoryEvent = {
      target: {
        name: `${fieldName}Category`,
        value: selectedCategory
      }
    };
    handleInputChange(categoryEvent);
    
    // Clear the corresponding topic
    const topicEvent = {
      target: {
        name: fieldName,
        value: ""
      }
    };
    handleInputChange(topicEvent);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested object updates (same logic as Profile component)
      const keys = name.split('.');
      setFormData(prev => {
        const updated = { ...prev };
        let current = updated;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {};
          current = current[keys[i]];
        }
        
        current[keys[keys.length - 1]] = value;
        
        // Clear rankOther if rank is changed to something other than "Other"
        if (keys[keys.length - 1] === 'rank' && value !== "Other") {
          current.rankOther = '';
        }
        
        return updated;
      });
    } else if (name.match(/^(primary[123])$/)) {
      // Handle areas of expertise topic selection with AreasOfExpertise naming pattern
      setFormData(prev => {
        // Ensure areasOfExpertise structure exists
        const currentAreas = prev.areasOfExpertise || { primary: ["", "", ""] };
        
        let updated = { ...prev, [name]: value, areasOfExpertise: { ...currentAreas } };
        
        // Map the field name to array position
        if (name === 'primary1') {
          updated.areasOfExpertise.primary[0] = value;
        } else if (name === 'primary2') {
          updated.areasOfExpertise.primary[1] = value;
        } else if (name === 'primary3') {
          updated.areasOfExpertise.primary[2] = value;
        }
        
        return updated;
      });
    } else if (name.includes('Category')) {
      // Handle category changes for expertise - clear corresponding topic
      setFormData(prev => {
        const updated = { ...prev, [name]: value };
        
        // Ensure areasOfExpertise structure exists
        if (!updated.areasOfExpertise) {
          updated.areasOfExpertise = { primary: ["", "", ""] };
        }
        if (!updated.areasOfExpertise.primary) {
          updated.areasOfExpertise.primary = ["", "", ""];
        }
        
        // Clear the corresponding topic when category changes
        const fieldName = name.replace('Category', '');
        if (fieldName === 'primary1') {
          updated.areasOfExpertise.primary[0] = '';
          updated.primary1 = '';
        } else if (fieldName === 'primary2') {
          updated.areasOfExpertise.primary[1] = '';
          updated.primary2 = '';
        } else if (fieldName === 'primary3') {
          updated.areasOfExpertise.primary[2] = '';
          updated.primary3 = '';
        }
        
        return updated;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Helper function to create grouped research areas options (same as Profile)
  const createGroupedOptions = () => {
    const options = [];
    Object.keys(researchAreas).forEach(category => {
      options.push(
        <optgroup key={category} label={category}>
          {researchAreas[category].map(subcategory => (
            <option key={`${category}-${subcategory}`} value={subcategory}>
              {subcategory}
            </option>
          ))}
        </optgroup>
      );
    });
    return options;
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateUser(user._id, formData);
      showAlertMessage('User updated successfully');
      onUserUpdated();
      onClose();
    } catch (error) {
      showAlertMessage(
        error.response?.data?.message || 'Error updating user',
        'danger'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!generateRandomPassword && !manualPassword) {
      showAlertMessage('Please either generate a random password or enter a password manually', 'danger');
      return;
    }

    setIsLoading(true);
    try {
      const payload = generateRandomPassword 
        ? { generateRandom: true }
        : { newPassword: manualPassword };

      const response = await resetUserPassword(user._id, payload);
      
      if (generateRandomPassword && response.data.newPassword) {
        setNewPassword(response.data.newPassword);
        showAlertMessage(`Password reset successfully. New password: ${response.data.newPassword}`);
      } else {
        showAlertMessage('Password reset successfully.');
      }
      
      setShowPasswordResetConfirm(false);
      setManualPassword('');
    } catch (error) {
      console.error('Password reset error:', error);
      showAlertMessage(
        error.response?.data?.message || 'Error resetting password',
        'danger'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal show={isOpen} onHide={onClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User: {user?.firstName} {user?.lastName}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {showAlert && (
            <Alert variant={alertType} dismissible onClose={() => setShowAlert(false)}>
              {alertMessage}
            </Alert>
          )}

          {newPassword && (
            <Alert variant="info">
              <strong>New Password:</strong> <code>{newPassword}</code>
            </Alert>
          )}

          <Tabs defaultActiveKey="personal" id="user-edit-tabs" className="mb-3">
            <Tab eventKey="personal" title="Personal Info">
              <Form>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Salutation *</Form.Label>
                      <Form.Select
                        name="salutation"
                        value={formData.salutation}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select...</option>
                        {salutations.map((sal) => (
                          <option key={sal} value={sal}>{sal}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>First Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Middle Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="middleName"
                        value={formData.middleName}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Last Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Primary Email *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Secondary Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="secondaryEmail"
                        value={formData.secondaryEmail}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Work Phone</Form.Label>
                      <div className="d-flex">
                        <Form.Select
                          style={{ maxWidth: "100px" }}
                          name="workPhoneCountry"
                          value={formData.workPhoneCountry}
                          onChange={handleInputChange}
                          className="me-2"
                        >
                          {countryCodes.map((code) => (
                            <option key={code} value={code}>{code}</option>
                          ))}
                        </Form.Select>
                        <Form.Control
                          type="tel"
                          name="workPhone"
                          value={formData.workPhone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Cell Phone</Form.Label>
                      <div className="d-flex">
                        <Form.Select
                          style={{ maxWidth: "100px" }}
                          name="cellPhoneCountry"
                          value={formData.cellPhoneCountry}
                          onChange={handleInputChange}
                          className="me-2"
                        >
                          {countryCodes.map((code) => (
                            <option key={code} value={code}>{code}</option>
                          ))}
                        </Form.Select>
                        <Form.Control
                          type="tel"
                          name="cellPhone"
                          value={formData.cellPhone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Personal Web Page</Form.Label>
                  <Form.Control
                    type="url"
                    name="personalWebPage"
                    value={formData.personalWebPage}
                    onChange={handleInputChange}
                    placeholder="http://"
                  />
                </Form.Group>
              </Form>
            </Tab>

            <Tab eventKey="affiliations" title="Affiliations">
              <Form>
                <h6 className="text-primary mb-3">Primary Affiliation</h6>
                
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Institution <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="primaryAffiliation.institution"
                        value={formData.primaryAffiliation?.institution || ''}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Academic Rank <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="primaryAffiliation.rank"
                        value={formData.primaryAffiliation?.rank || ''}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select rank...</option>
                        {academicRanks.map((rank) => (
                          <option key={rank} value={rank}>{rank}</option>
                        ))}
                      </Form.Select>
                      
                      {showPrimaryOtherRank && (
                        <div className="mt-2">
                          <Form.Label>Please specify other rank</Form.Label>
                          <Form.Control
                            type="text"
                            name="primaryAffiliation.rankOther"
                            value={formData.primaryAffiliation?.rankOther || ''}
                            onChange={handleInputChange}
                            placeholder="Please specify your rank"
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Department</Form.Label>
                      <Form.Control
                        type="text"
                        name="primaryAffiliation.department"
                        value={formData.primaryAffiliation?.department || ''}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Address 1 <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="primaryAffiliation.address.address1"
                        value={formData.primaryAffiliation?.address?.address1 || ''}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>City <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="primaryAffiliation.address.city"
                        value={formData.primaryAffiliation?.address?.city || ''}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Postal Code <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="primaryAffiliation.address.postalCode"
                        value={formData.primaryAffiliation?.address?.postalCode || ''}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="primaryAffiliation.address.country"
                        value={formData.primaryAffiliation?.address?.country || ''}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Country...</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {formData.primaryAffiliation?.address?.country === "United States" && (
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <Form.Select
                          name="primaryAffiliation.address.state"
                          value={formData.primaryAffiliation?.address?.state || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">Select State...</option>
                          {usStates.map((state) => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <hr className="my-4" />

                <h6 className="text-primary mb-3">Secondary Affiliation</h6>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Institution</Form.Label>
                      <Form.Control
                        type="text"
                        name="secondaryAffiliation.institution"
                        value={formData.secondaryAffiliation?.institution || ''}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Academic Rank</Form.Label>
                      <Form.Select
                        name="secondaryAffiliation.rank"
                        value={formData.secondaryAffiliation?.rank || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select rank...</option>
                        {academicRanks.map((rank) => (
                          <option key={rank} value={rank}>{rank}</option>
                        ))}
                      </Form.Select>
                      
                      {showSecondaryOtherRank && (
                        <div className="mt-2">
                          <Form.Label>Please specify other rank</Form.Label>
                          <Form.Control
                            type="text"
                            name="secondaryAffiliation.rankOther"
                            value={formData.secondaryAffiliation?.rankOther || ''}
                            onChange={handleInputChange}
                            placeholder="Please specify your rank"
                          />
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Department</Form.Label>
                      <Form.Control
                        type="text"
                        name="secondaryAffiliation.department"
                        value={formData.secondaryAffiliation?.department || ''}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Address 1</Form.Label>
                      <Form.Control
                        type="text"
                        name="secondaryAffiliation.address.address1"
                        value={formData.secondaryAffiliation?.address?.address1 || ''}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        name="secondaryAffiliation.address.city"
                        value={formData.secondaryAffiliation?.address?.city || ''}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Postal Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="secondaryAffiliation.address.postalCode"
                        value={formData.secondaryAffiliation?.address?.postalCode || ''}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Country</Form.Label>
                      <Form.Select
                        name="secondaryAffiliation.address.country"
                        value={formData.secondaryAffiliation?.address?.country || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Country...</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {formData.secondaryAffiliation?.address?.country === "United States" && (
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <Form.Select
                          name="secondaryAffiliation.address.state"
                          value={formData.secondaryAffiliation?.address?.state || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">Select State...</option>
                          {usStates.map((state) => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Form>
            </Tab>

            <Tab eventKey="expertise" title="Expertise">
              <Form>
                <h6 className="text-primary mb-3">Primary Areas *</h6>
                
                <div className="mb-3">
                  <Form.Group>
                    <Form.Label>Primary 1 - Category *</Form.Label>
                    <Form.Select
                      name="primary1Category"
                      value={formData.primary1Category || ""}
                      onChange={(e) => handleCategoryChange(e, "primary1")}
                      required
                    >
                      <option value="">Select category...</option>
                      {Object.keys(researchAreas).map((category, index) => (
                        <option key={index} value={category}>
                          {category}
                        </option>
                      ))}
                    </Form.Select>
                    
                    {formData.primary1Category && (
                      <div className="ms-4 mt-2">
                        <Form.Label>Primary 1 - Topic *</Form.Label>
                        <Form.Select
                          name="primary1"
                          value={formData.primary1 || ""}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select topic...</option>
                          {researchAreas[formData.primary1Category]?.map((topic, index) => (
                            <option key={index} value={topic}>
                              {topic}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    )}
                  </Form.Group>
                </div>

                <div className="mb-3">
                  <Form.Group>
                    <Form.Label>Primary 2 - Category *</Form.Label>
                    <Form.Select
                      name="primary2Category"
                      value={formData.primary2Category || ""}
                      onChange={(e) => handleCategoryChange(e, "primary2")}
                      required
                    >
                      <option value="">Select category...</option>
                      {Object.keys(researchAreas).map((category, index) => (
                        <option key={index} value={category}>
                          {category}
                        </option>
                      ))}
                    </Form.Select>
                    
                    {formData.primary2Category && (
                      <div className="ms-4 mt-2">
                        <Form.Label>Primary 2 - Topic *</Form.Label>
                        <Form.Select
                          name="primary2"
                          value={formData.primary2 || ""}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select topic...</option>
                          {researchAreas[formData.primary2Category]?.map((topic, index) => (
                            <option key={index} value={topic}>
                              {topic}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    )}
                  </Form.Group>
                </div>

                <div className="mb-3">
                  <Form.Group>
                    <Form.Label>Primary 3 - Category *</Form.Label>
                    <Form.Select
                      name="primary3Category"
                      value={formData.primary3Category || ""}
                      onChange={(e) => handleCategoryChange(e, "primary3")}
                      required
                    >
                      <option value="">Select category...</option>
                      {Object.keys(researchAreas).map((category, index) => (
                        <option key={index} value={category}>
                          {category}
                        </option>
                      ))}
                    </Form.Select>
                    
                    {formData.primary3Category && (
                      <div className="ms-4 mt-2">
                        <Form.Label>Primary 3 - Topic *</Form.Label>
                        <Form.Select
                          name="primary3"
                          value={formData.primary3 || ""}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select topic...</option>
                          {researchAreas[formData.primary3Category]?.map((topic, index) => (
                            <option key={index} value={topic}>
                              {topic}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    )}
                  </Form.Group>
                </div>

                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  <small>
                    Please select your three primary areas of expertise by first choosing the category, then the specific topic. 
                    You can optionally add secondary areas using the same process.
                  </small>
                </div>
              </Form>
            </Tab>

            <Tab eventKey="account" title="Account">
              <Form>
                <Row className="mb-3">
                  {/* <Col md={6}>
                    <Form.Group>
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col> */}
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="reviewer">Reviewer</option>
                        <option value="moderator">Moderator</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="text-primary mb-3">Password Management</h6>
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  Use the "Reset Password" button below to change the user's password.
                </div>
              </Form>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => setShowPasswordResetConfirm(true)}
          >
            Reset Password
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Password Reset Confirmation Modal */}
      <Modal 
        show={showPasswordResetConfirm} 
        onHide={() => setShowPasswordResetConfirm(false)}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to reset the password for {user?.firstName} {user?.lastName}?</p>
          
          <Form.Check
            type="checkbox"
            label="Generate random password (will be displayed after reset)"
            checked={generateRandomPassword}
            onChange={(e) => {
              setGenerateRandomPassword(e.target.checked);
              if (e.target.checked) {
                setManualPassword('');
              }
            }}
            className="mb-3"
          />
          
          {!generateRandomPassword && (
            <div className="mt-3">
              <Form.Group>
                <Form.Label>Enter New Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter the new password"
                  value={manualPassword}
                  onChange={(e) => setManualPassword(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Enter a password for the user. They can change it after logging in.
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPasswordResetConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handlePasswordReset}
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserEditModal;
